/* eslint-disable react-hooks/exhaustive-deps -- effets alignés chargement id */
import React, { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { NS, U3144ROLES } from "../U3144";
import U3144LotDetailOperationTypeBody from "./form/u3144LotDetailOperationType/U3144LotDetailOperationTypeBody";
import { U3144LotDetailOperationTypeFormToolBar } from "./form/u3144LotDetailOperationType/U3144LotDetailOperationTypeFormToolBar";
import U3144LotDetailOperationTypeDeleteDlg from "./common/U3144LotDetailOperationTypeDeleteDlg";
import { useU3144Context } from "../U3144Context";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  createEmptyU3144FormRow,
  luHandleAppiError,
  normalizeU3144FromApi,
  type U3144LotDetailOperationType,
  type U3144LotDetailOperationTypeFormRow,
  type U3144LotDetailOperationTypeSearchRow,
} from "../U3144Service";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";

function translationFieldKey(index: number, field: "languageCode" | "operationTypeLabel"): string {
  return `translations.${index}.${field}`;
}

function normalizeForForm(row: U3144LotDetailOperationType): U3144LotDetailOperationTypeFormRow {
  return normalizeU3144FromApi(row);
}

function validateU3144Form(row: U3144LotDetailOperationTypeFormRow, t: (key: string, opts?: Record<string, unknown>) => string, NS: string, isFr: boolean): Record<string, string> {
  const result: Record<string, string> = {};
  const code = row.operationTypeCode?.trim() ?? "";
  if (!code) {
    result["operationTypeCode"] = t("errOperationTypeCodeRequired", {
      ns: NS,
      defaultValue: isFr ? "Le code est obligatoire." : "Code is required.",
    });
  } else if (code.length > 32) {
    result["operationTypeCode"] = t("errOperationTypeCodeMax", {
      ns: NS,
      defaultValue: isFr ? "Le code ne peut pas dépasser 32 caractères." : "Code cannot exceed 32 characters.",
    });
  }

  if (!row.translations || row.translations.length === 0) {
    result["translations"] = t("errTranslationsMin", {
      ns: NS,
      defaultValue: isFr ? "Au moins une traduction est requise." : "At least one translation is required.",
    });
    return result;
  }

  const allowedLangs = new Set(["fr", "en"]);
  const langsSeen = new Set<string>();
  row.translations.forEach((tr, i) => {
    const lang = tr.languageCode?.trim().toLowerCase() ?? "";
    if (!lang) {
      result[translationFieldKey(i, "languageCode")] = t("errLanguageCodeRequired", {
        ns: NS,
        defaultValue: isFr ? "La langue est obligatoire." : "Language is required.",
      });
    } else if (!allowedLangs.has(lang)) {
      result[translationFieldKey(i, "languageCode")] = t("errLanguageCodeChoice", {
        ns: NS,
        defaultValue: isFr ? "Choisissez FR ou EN." : "Choose FR or EN.",
      });
    } else if (langsSeen.has(lang)) {
      result[translationFieldKey(i, "languageCode")] = t("errLanguageCodeDuplicate", {
        ns: NS,
        defaultValue: isFr ? "Chaque code langue ne doit apparaître qu'une seule fois." : "Each language code must be unique.",
      });
    } else {
      langsSeen.add(lang);
    }

    const label = tr.operationTypeLabel?.trim() ?? "";
    if (!label) {
      result[translationFieldKey(i, "operationTypeLabel")] = t("errOperationTypeLabelRequired", {
        ns: NS,
        defaultValue: isFr ? "Le libellé est obligatoire." : "Label is required.",
      });
    } else if (label.length > 255) {
      result[translationFieldKey(i, "operationTypeLabel")] = t("errOperationTypeLabelMax", {
        ns: NS,
        defaultValue: isFr ? "Le libellé ne peut pas dépasser 255 caractères." : "Label cannot exceed 255 characters.",
      });
    }

    const hasId = Boolean(tr.id && String(tr.id).trim() !== "");
    if (hasId && (tr.version === undefined || tr.version === null || !Number.isFinite(Number(tr.version)))) {
      result[`translations.${i}.version`] = t("errTranslationVersionRequired", {
        ns: NS,
        defaultValue: isFr ? "Version manquante pour une traduction existante." : "Missing version for an existing translation.",
      });
    }
  });

  return result;
}

export interface U3144LotDetailOperationTypeFormExtendedHandlers extends U2000FormStateHandlersReturn<U3144LotDetailOperationTypeFormRow> {
  addNewLotDetailOperationType: () => void;
  modifyLotDetailOperationType: () => void;
  deleteLotDetailOperationType: () => Promise<void>;
}

const U3144LotDetailOperationTypeForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3144Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = (i18n.resolvedLanguage ?? i18n.language).startsWith("fr");
  const listTitle = t("title", { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const u3144BL: U2000FormSBusinessHandlers<U3144LotDetailOperationTypeFormRow> = {
    newRecord: () => createEmptyU3144FormRow(),

    validateForm: (row: U3144LotDetailOperationTypeFormRow) => validateU3144Form(row, t, NS, isFr),

    saveForme: async (row: U3144LotDetailOperationTypeFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateU3144Form(row, t, NS, isFr);
      if (Object.keys(errors).length > 0) {
        u3144FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },

    cancelForm: () => {
      const old = u3144FormExtended.getOldData() as U3144LotDetailOperationTypeFormRow | null;
      if (old) {
        u3144FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === "/U3144") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },

    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    onLeaveForm: () => {
      navigate("/U3144");
    },
  };

  const u3144FormHandlers = useU2000FormStateHandlers<U3144LotDetailOperationTypeFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3144BL,
  });

  const u3144FormExtended: U3144LotDetailOperationTypeFormExtendedHandlers = {
    ...u3144FormHandlers,
    addNewLotDetailOperationType: () => {
      u3144FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyLotDetailOperationType: () => {
      u3144FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteLotDetailOperationType: async () => {
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3144LotDetailOperationTypeSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3144LotDetailOperationTypeSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3144LotDetailOperationTypeFormRow>({
    doGetFn: (rowId: string) => {
      return doGetData(u2000AxiosAuthenticated, rowId);
    },
    onSuccess: (row: U3144LotDetailOperationTypeFormRow) => {
      const normalized = normalizeForForm(row as unknown as U3144LotDetailOperationType);
      gridStateHandlers.setDataById(normalized.id, normalized as unknown as U3144LotDetailOperationTypeSearchRow);
      u3144FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      const firstLabel = normalized.translations?.[0]?.operationTypeLabel ?? "";
      const crumbLabel = firstLabel ? `${normalized.operationTypeCode} — ${firstLabel}` : normalized.operationTypeCode;
      setLogicalUnitBreadcrumb([{ href: "/U3144", label: listTitle }, { label: crumbLabel }]);

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3144FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3144LotDetailOperationTypeFormRow>({
    doSaveFn: (row: U3144LotDetailOperationTypeFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U3144LotDetailOperationTypeFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeForForm(row as unknown as U3144LotDetailOperationType);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), normalized as unknown as U3144LotDetailOperationTypeSearchRow]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(row.id, normalized as unknown as U3144LotDetailOperationTypeSearchRow);
      }
      u3144FormExtended.afterSaveData(normalized);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3144") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3144FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3144FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3144FormExtended.getData()],
    doDeleteFn: async (rowId, row) => {
      const r = row as U3144LotDetailOperationTypeFormRow;
      return await doDeleteData(u2000AxiosAuthenticated, rowId, buildDeleteBody(r));
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((del) => {
        toast.success(t("success0003", { key: (del as U3144LotDetailOperationTypeFormRow).operationTypeCode, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3144", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u3144FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3144FormExtended.setData(createEmptyU3144FormRow());
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  if (u3144FormExtended.getData() === null) {
    return null;
  }

  const formTabs = [
    hasRole(U3144ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: (
        <U2000AuditPanelComponent
          id={u3144FormExtended.getData()?.id}
          doGetAuditsData={doGetAuditsData}
          toastName="U3144LotDetailOperationTypeAudit"
        />
      ),
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U3144LotDetailOperationTypeDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3144LotDetailOperationTypeFormToolBar formStateHandlers={u3144FormExtended} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3144FormExtended as any}
      >
        <U3144LotDetailOperationTypeBody formStateHandlers={u3144FormExtended} t={t} />

        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab={formTabs[0]?.id}
            activeTab={activeTab ?? formTabs[0]?.id}
            setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3144LotDetailOperationTypeForm;
